import React, { useState } from 'react';
// Import Material-UI components individually to avoid bundling issues
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Code } from '@mui/icons-material';
// Import theming separately to ensure proper loading order
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Import environment configuration
import { env, isDevelopment } from './config/env';

// Import tab components
import { BuildProcessTab } from './components/BuildProcessTab';
import { WhyMigrateCRATab } from './components/WhyMigrateCRATab';
import { MigrationOptionsTab } from './components/MigrationOptionsTab';
import { PotentialSideEffectsTab } from './components/PotentialSideEffectsTab';
import { StepByStepGuideTab } from './components/StepByStepGuideTab';
import { CommonQuestionsTab } from './components/CommonQuestionsTab';
import { AboutTab } from './components/AboutTab';
import { DynamicFavicon } from './components/DynamicFavicon';

// Simple theme creation without complex configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

function App() {
  const [tabValue, setTabValue] = useState(0);
  const [activeStep, setActiveStep] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const handleNavigateToStepByStep = () => {
    setTabValue(4); // Navigate to Step-by-Step Guide tab
    setActiveStep(0); // Reset to first step
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DynamicFavicon />
      <AppBar position="static">
        <Toolbar>
          <Code sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            React Build Tools & Migration Guide
          </Typography><Typography variant="body2" component="div" sx={{ flexGrow: 0 }}>
            v{env.version} ({env.environment})
            {isDevelopment && ' - DEV'}
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="migration guide tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-flexContainer': {
                '@media (min-width: 1200px)': {
                  justifyContent: 'space-between',
                  width: '100%',
                },
              },
              '& .MuiTab-root': {
                '@media (min-width: 1200px)': {
                  flex: 1,
                  maxWidth: 'none',
                },
              },
            }}
          >
            <Tab label="Build Process" {...a11yProps(0)} />
            <Tab label="Why Migrate?" {...a11yProps(1)} />
            <Tab label="Migration Options" {...a11yProps(2)} />
            <Tab label="Potential Side Effects" {...a11yProps(3)} />
            <Tab label="Step-by-Step Guide" {...a11yProps(4)} />
            <Tab label="Common Questions" {...a11yProps(5)} />
            <Tab label="About" {...a11yProps(6)} />
          </Tabs>
        </Box>

        <CustomTabPanel value={tabValue} index={0}>
          <BuildProcessTab />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={1}>
          <WhyMigrateCRATab />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={2}>
          <MigrationOptionsTab onNavigateToStepByStep={handleNavigateToStepByStep} />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={3}>
          <PotentialSideEffectsTab />
        </CustomTabPanel>
        <CustomTabPanel value={tabValue} index={4}>
          <StepByStepGuideTab
            activeStep={activeStep}
            handleNext={handleNext}
            handleBack={handleBack}
          />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={5}>
          <CommonQuestionsTab />
        </CustomTabPanel>

        <CustomTabPanel value={tabValue} index={6}>
          <AboutTab />
        </CustomTabPanel>
      </Container>
    </ThemeProvider>
  );
}

export default App;
